import React, { useState, createContext, useCallback, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";

import { PREFIX } from "../../constants";
// Import your Firebase auth instance from your config file
import {
    signInWithEmailAndPassword,
    signOut,
    reauthenticateWithCredential,
    EmailAuthProvider,
    updatePassword,
    onAuthStateChanged
} from "firebase/auth";
import {userManagementAuth} from "../../firebaseConfig";
import {toast} from "react-toastify";

export const AuthenticationContext = createContext();
// Use the imported auth instance instead of getAuth()
const auth = userManagementAuth;
const withOperatorDefaults = (user) => ({
    ...user,
    ["operator/manages"]: user?.["operator/manages"] || [],
});

export const AuthenticationContextProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [firebaseSessionUser, setFirebaseSessionUser] = useState(null);
    const [sessionError, setSessionError] = useState(null);
    const [errorMes, setErrorMes] = useState(null);
    const [success, setSuccess] = useState(false);
    const [freshLogin, setFreshLogin] = useState(false);
    const [isLoading, setIsLoading] = useState(true); // Start with true to show loading initially
    const [authInitialized, setAuthInitialized] = useState(false); // Track if Firebase auth is initialized
    const [userCredentials, setUserCredentials] = useState({
        full_name: "",
        email: "",
        password: "",
        profilePhoto: null,
        menuImg: null,
        menuLink: "",
    });

    const renewOperatorSession = useCallback(async (firebaseUser) => {
        if (!firebaseUser) throw new Error("No Firebase user is signed in");
        const idToken = await firebaseUser.getIdToken();
        const response = await axios.post(`${PREFIX}/api/user/refresh-session`, {}, {
            headers: { Authorization: `Bearer ${idToken}` },
        });
        const user = withOperatorDefaults(response.data.user);
        setCurrentUser(user);
        setFirebaseSessionUser(firebaseUser);
        setSessionError(null);
        return user;
    }, []);

    const handleSessionFailure = useCallback(async (error, firebaseUser) => {
        const invalidFirebaseCodes = [
            "auth/user-token-expired",
            "auth/user-disabled",
            "auth/user-not-found",
            "auth/invalid-user-token",
        ];
        if ([401, 403].includes(error.response?.status) || invalidFirebaseCodes.includes(error.code)) {
            try {
                await signOut(auth);
            } catch (signOutError) {
                console.error("Could not clear invalid Firebase session:", signOutError);
            }
            setCurrentUser(null);
            setFirebaseSessionUser(null);
            setSessionError(null);
            Cookies.remove("userId");
            return;
        }

        setFirebaseSessionUser(firebaseUser || auth.currentUser);
        setSessionError("Knowbie is temporarily unavailable. Your sign-in is still active.");
    }, []);

    const retrySession = useCallback(async () => {
        const firebaseUser = auth.currentUser;
        if (!firebaseUser) return;
        setIsLoading(true);
        setSessionError(null);
        try {
            await renewOperatorSession(firebaseUser);
        } catch (error) {
            console.error("Operator session refresh failed:", error);
            await handleSessionFailure(error, firebaseUser);
        } finally {
            setIsLoading(false);
            setAuthInitialized(true);
        }
    }, [handleSessionFailure, renewOperatorSession]);

    // Firebase is the durable login. Renew the API cookie from its verified
    // ID token, and leave the Firebase session intact during server outages.
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            setFirebaseSessionUser(firebaseUser || null);
            if (!firebaseUser) {
                setCurrentUser(null);
                setSessionError(null);
                Cookies.remove("userId");
                setIsLoading(false);
                setAuthInitialized(true);
                return;
            }

            setIsLoading(true);
            try {
                await renewOperatorSession(firebaseUser);
            } catch (error) {
                console.error("Error refreshing Operator session:", error);
                await handleSessionFailure(error, firebaseUser);
            } finally {
                setIsLoading(false);
                setAuthInitialized(true);
            }
        });

        return () => unsubscribe();
    }, [handleSessionFailure, renewOperatorSession]);

    const setErrorMessage = err => {
        setErrorMes(err);

        setTimeout(() => {
            setErrorMes(null);
        }, 5000);
    };

    const setSuccessFunc = () => {
        setSuccess(true);

        setTimeout(() => {
            setSuccess(false);
        }, 10000);
    };

    const setNewPasswordAndUpdateInfo = ({ password, status }) => {
        setIsLoading(true);
        const formData = new FormData();
        const email = userCredentials.email.toLowerCase();
        formData.append("email", userCredentials.email.toLowerCase());
        formData.append("password", password);
        formData.append("full_name", userCredentials.full_name);
        formData.append(
            "profilePhoto",
            !!userCredentials.profilePhoto ? userCredentials.profilePhoto : null,
        );
        formData.append(
            "menuPhoto",
            !!userCredentials.menuImg ? userCredentials.menuImg : null,
        );

        formData.append(
            "menuLink",
            userCredentials.menuLink.length > 0 ? userCredentials.menuLink : null,
        );

        formData.append("status", status);

        if(!auth.currentUser)
            return setErrorMessage("Session expired. Please log in again.");

        updatePassword(auth.currentUser, password)
            .then(async res => {
                const idToken = await auth.currentUser.getIdToken();
                axios
                    .post("/api/user/update-operator-info", formData, {
                        headers: { Authorization: `Bearer ${idToken}` },
                    })
                    .then(res => {
                        setErrorMes(null);
                    })
                    .then(() => {
                        logInUser(email, password);
                    })
                    .catch(err => {
                        setIsLoading(false);
                        console.error(err);
                    });
            })
            .catch(err => {
                setIsLoading(false);
                console.error(err);
            });
    };

    const logInUser = (email, password, setSuccessStatus = false) => {
        setIsLoading(true);

        signInWithEmailAndPassword(
            auth,
            email.toLowerCase(),
            password
        ).then(async firebaseResult => {
            try {
                const user = await renewOperatorSession(firebaseResult.user);
                    Cookies.set("userId", user._id, { expires: 365 });

                    // Set fresh login flag for cache refresh
                    setFreshLogin(true);

                    // Clear existing caches to ensure fresh data on dashboard
                    localStorage.removeItem('announcementsLastFetch');
                    localStorage.removeItem('customAnnouncementDraft');
                    localStorage.removeItem('preshiftAnnouncementDraft');

                    if (setSuccessStatus) setSuccess(true);
                    setErrorMes(null);
            } catch (error) {
                await handleSessionFailure(error, firebaseResult.user);
                setErrorMes(error.response?.data?.message || "Login failed");
            } finally {
                setIsLoading(false);
            }
        })
            .catch(err => {
                setIsLoading(false);
                setErrorMessage("Incorrect email or password");
            });
    };

    // Helper function to clear fresh login flag after cache refresh is complete
    const clearFreshLogin = () => {
        setFreshLogin(false);
    };

    const changePassword = async (email, oldPassword, newPassword) => {
        const user = auth.currentUser;

        if (!user) {
            setErrorMessage("No user is currently logged in");
            return;
        }

        try {
            // Reauthenticate user with old password
            const credential = EmailAuthProvider.credential(email.toLowerCase(), oldPassword);
            await reauthenticateWithCredential(user, credential);

            // Update to new password
            await updatePassword(user, newPassword);

            setSuccessFunc();
        } catch (error) {
            if (error.code === "auth/wrong-password") {
                setErrorMessage("Your old password is incorrect");
            } else {
                setErrorMessage("Something went wrong");
            }
            console.error(error);
        }
    };

    // Refresh the API cookie from Firebase instead of treating a failed API
    // lookup as proof that the durable Firebase session is invalid.
    const checkSession = useCallback(async () => {
        if (!authInitialized) {
            return; // Wait for Firebase auth to initialize
        }
        const firebaseUser = auth.currentUser;
        if (!firebaseUser) return;
        setIsLoading(true);
        try {
            await renewOperatorSession(firebaseUser);
        } catch (error) {
            console.error("Session refresh failed:", error);
            await handleSessionFailure(error, firebaseUser);
        } finally {
            setIsLoading(false);
        }
    }, [authInitialized, handleSessionFailure, renewOperatorSession]);

    // Sign out function
    const signOutUser = async () => {
        try {
            await signOut(auth);
            setCurrentUser(null);
            setFirebaseSessionUser(null);
            setSessionError(null);

            // Clear localStorage
            localStorage.removeItem('announcementsLastFetch');
            localStorage.removeItem('customAnnouncementDraft');
            localStorage.removeItem('preshiftAnnouncementDraft');

            // Clear cookies
            Cookies.remove('userId');

            return { success: true };
        } catch (error) {
            console.error("Error signing out:", error);
            setCurrentUser(null);
            return { success: false, error };
        }
    };

    const recoveryPasswordFunc = async (operEmail) => {
        try {
            await axios.post("/api/user/send-password-reset", { email: operEmail });
            setSuccessFunc();
        } catch (err) {
            setErrorMessage("Something went wrong. Try again.");
        }
    };

    const changeMenuLink = updateMenuObj => {
        return new Promise((resolve, reject) => {
            const toastId = toast.loading('Updating Menu...', { position: "top-center" });

            const formData = new FormData();

            formData.append(
                "menuPhoto",
                !!updateMenuObj.menuImg ? updateMenuObj.menuImg : null,
            );

            formData.append("menuLink", updateMenuObj.menuLink);
            formData.append("operatorId", updateMenuObj.operatorId);

            axios
                .put("/api/user/update-menu-link", formData)
                .then(res => {
                    setErrorMes(null);
                    setCurrentUser({ ...res.data.user });
                    toast.update(toastId, {
                        render: `Menu updated successfully!`,
                        type: 'success',
                        isLoading: false,
                        autoClose: 5000,
                        closeButton: true
                    });
                    resolve(res); // Resolve the promise
                })
                .catch(err => {
                    toast.update(toastId, {
                        render: err.response?.data?.message || "Failed to update menu. Please try again.",
                        type: 'error',
                        isLoading: false,
                        autoClose: 5000,
                        closeButton: true
                    });
                    console.error(err);
                    reject(err); // Reject the promise
                });
        });
    };
    return (
        <AuthenticationContext.Provider
            value={{
                setErrorMes,
                userCredentials,
                setUserCredentials,
                errorMes,
                setNewPasswordAndUpdateInfo,
                logInUser,
                changeMenuLink,
                currentUser,
                firebaseSessionUser,
                sessionError,
                retrySession,
                setCurrentUser,
                success,
                checkSession,
                changePassword,
                setErrorMessage,
                setSuccess,
                recoveryPasswordFunc,
                isLoading,
                freshLogin,
                clearFreshLogin,
                signOutUser,
                authInitialized, // Add this so components can check if auth is ready
            }}>
            {children}
        </AuthenticationContext.Provider>
    );
};
